import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { parseLocalDate } from '../../../core/date-utils';
import { InvoiceService, type PublicInvoiceDetailDTO, type ChargeDTO } from '../../../services/invoice.service';
import { TagModule } from 'primeng/tag';

@Component({
  selector: 'app-public-receipt',
  templateUrl: './public-receipt.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TagModule]
})
export class PublicReceiptComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private invoiceService = inject(InvoiceService);

  detail = signal<PublicInvoiceDetailDTO | null>(null);
  charge = signal<ChargeDTO | null>(null);
  loading = signal(true);
  error = signal(false);
  paymentLoading = signal(false);

  ngOnInit(): void {
    const chargeIdParam = this.route.snapshot.paramMap.get('chargeId');
    const hash = this.route.snapshot.queryParamMap.get('hash');

    const chargeId = chargeIdParam ? Number(chargeIdParam) : null;

    if (!chargeId || Number.isNaN(chargeId) || !hash) {
      this.loading.set(false);
      this.error.set(true);
      return;
    }

    this.invoiceService.getPublicChargeReceiptDetail(chargeId, hash).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (!res?.body) {
          this.error.set(true);
          return;
        }
        this.detail.set(res.body);
        const c = res.body.charges?.find((ch) => ch.id === chargeId) ?? null;
        this.charge.set(c);
        if (!c) this.error.set(true);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      }
    });
  }

  isChargePaid(charge: ChargeDTO): boolean {
    return charge?.status === 'PAID';
  }

  isChargePending(charge: ChargeDTO): boolean {
    return charge?.status === 'PENDING' || charge?.status === 'DUE' || charge?.status === 'SCHEDULED';
  }

  /** Cargo cancelado o expirado: la liga de pago ya no es válida. */
  isChargeCancelled(charge: ChargeDTO): boolean {
    return charge?.status === 'CANCELLED' || charge?.status === 'EXPIRED';
  }

  /** Recibo pendiente vencido: cargo en estado DUE. */
  isChargeOverdueReceipt(charge: ChargeDTO): boolean {
    return charge?.status === 'DUE';
  }

  getPaymentLinkUrl(charge: ChargeDTO): string {
    return charge?.paymentLink?.link ?? '#';
  }

  formatAmount(amount: number | undefined, currency: string): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency || 'MXN'
    }).format(amount ?? 0);
  }

  formatDate(isoDate: string | undefined): string {
    if (!isoDate) return '—';
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(isoDate));
  }

  /** `dueDate` es LocalDate (`yyyy-MM-dd`); no usar UTC para no correr el día. */
  formatDueDate(dueDate: string | undefined): string {
    const date = parseLocalDate(dueDate);
    if (!date) return '—';
    return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(date);
  }

  getCustomerName(sale: PublicInvoiceDetailDTO['sale']): string {
    const c = sale?.customer;
    if (!c) return '—';
    return c.comercialName || c.legalName || [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
  }

  onPay(url: string, event: Event): void {
    if (!url || url === '#') return;
    event.preventDefault();
    this.paymentLoading.set(true);
    window.location.href = url;
  }
}
